/** HTTP statuses that indicate a safe automatic retry (pool pressure, gateway, rate limit). */
export const TRANSIENT_HTTP_STATUSES = new Set([429, 502, 503, 504]);

export type TransientErrorLike = {
  status?: number;
  retryAfterMs?: number;
  message?: string;
  name?: string;
};

export function parseRetryAfterHeader(value: string | null | undefined): number | undefined {
  if (!value) return undefined;
  const asSeconds = Number(value);
  if (Number.isFinite(asSeconds) && asSeconds >= 0) {
    return asSeconds * 1000;
  }
  const asDate = Date.parse(value);
  if (Number.isFinite(asDate)) {
    return Math.max(0, asDate - Date.now());
  }
  return undefined;
}

export function isTransientHttpStatus(status: number | undefined): boolean {
  return status != null && TRANSIENT_HTTP_STATUSES.has(status);
}

const TRANSIENT_MESSAGE_PATTERN =
  /temporarily unavailable|connection pool|pool is under pressure|service unavailable|gateway timeout|timed out|timeout|ETIMEDOUT|ECONNRESET|Failed to fetch|NetworkError|acquire-timeout/i;

export function isTransientApiError(error: unknown): boolean {
  if (!error) return false;
  const candidate = error as TransientErrorLike;
  if (isTransientHttpStatus(candidate.status)) return true;
  const message = candidate.message ?? (error instanceof Error ? error.message : String(error));
  return TRANSIENT_MESSAGE_PATTERN.test(message);
}

export function transientQueryShouldRetry(
  failureCount: number,
  error: unknown,
  maxRetries = 6,
): boolean {
  if (failureCount >= maxRetries) return false;
  const status = (error as TransientErrorLike)?.status;
  if (status === 404 || status === 401 || status === 403 || status === 409) return false;
  if (isTransientApiError(error)) return true;
  return false;
}

export function getTransientRetryDelayMs(attemptIndex: number, error: unknown): number {
  const retryAfterMs = (error as TransientErrorLike)?.retryAfterMs;
  if (retryAfterMs != null && retryAfterMs > 0) {
    return Math.min(retryAfterMs, 30_000);
  }
  const base = Math.min(30_000, 1000 * 2 ** attemptIndex);
  const jitter = Math.floor(Math.random() * 250);
  return base + jitter;
}

export const transientReadRetryOptions = {
  retry: (failureCount: number, error: unknown) => transientQueryShouldRetry(failureCount, error),
  retryDelay: (attemptIndex: number, error: unknown) => getTransientRetryDelayMs(attemptIndex, error),
} as const;

export function attachTransientMetadata<T extends TransientErrorLike>(
  error: T,
  status: number,
  retryAfterMs?: number,
): T {
  error.status = status;
  if (retryAfterMs != null) {
    error.retryAfterMs = retryAfterMs;
  }
  return error;
}
